import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  Empty,
  Image,
  Menu,
  Modal,
  Row,
  message,
} from "antd";
import {
  DownloadOutlined,
  FileExcelOutlined,
  FileOutlined,
  FilePdfOutlined,
  LinkOutlined,
  LoadingOutlined,
  PlayCircleOutlined,
} from "@ant-design/icons";
import ReactPlayer from "react-player";
import { AnimatePresence, motion } from "framer-motion";
import Flex from "components/shared-components/Flex";
import EllipsisDropdown from "components/shared-components/EllipsisDropdown";
import apiClient from "services/apiClient";
import MediaTypeFilter from "./MediaTypeFilter";
import { useMyContext } from "Context/MyContextProvider";
import { downloadReportMediaFile } from "../chat/fileDownloadUtils";
import AudioMessagePlayer from "../components/AudioMessagePlayer";

const copyToClipboard = (text) => {
  navigator.clipboard
    .writeText(text)
    .then(() => message.success("Link copied to clipboard"))
    .catch(() => message.error("Failed to copy link"));
};

const isAudioMedia = (mimeType, messagesType) =>
  mimeType.startsWith("audio/") || messagesType === "audio";

const resolveParentNumber = (userData, isSupportAgent) => {
  if (isSupportAgent) {
    return userData?.reporting_user?.whatsapp_number || userData?.whatsapp_number || "";
  }

  return userData?.whatsapp_number || "";
};

const ReportImagePreview = ({ data, api, authToken, parentNumber }) => {
  const [src, setSrc] = useState(data?.path || null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (data?.path) {
        setSrc(data.path);
        return;
      }

      if (!data?.media_id || !data?.report_id) {
        setSrc(null);
        return;
      }

      setLoading(true);
      try {
        const firstResponse = await apiClient.get(`messaging/media/${data.media_id}`);
        const metaUrl = firstResponse.data?.url;
        if (!metaUrl || cancelled) {
          return;
        }

        const secondResponse = await apiClient.post(
          `${api}retrieve-image-from-meta`,
          {
            url: metaUrl,
            parent_number: String(parentNumber || ""),
            child_number: String(data?.wa_id || ""),
            report_id: data.report_id,
            media_type: data?.messages_type || "image",
            mime_type: data?.type,
            file_name: data?.name,
          },
          {
            headers: authToken
              ? { Authorization: `Bearer ${authToken}` }
              : {},
          }
        );

        if (!cancelled) {
          setSrc(secondResponse?.data?.media_url || null);
        }
      } catch (error) {
        console.error("Failed to load report image preview", error);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [
    api,
    authToken,
    data?.media_id,
    data?.messages_type,
    data?.name,
    data?.path,
    data?.report_id,
    data?.type,
    data?.wa_id,
    parentNumber,
  ]);

  if (loading) {
    return (
      <Flex justifyContent="center" className="py-4">
        <LoadingOutlined style={{ fontSize: 28 }} />
      </Flex>
    );
  }

  if (!src) {
    return null;
  }

  return (
    <Image
      src={src}
      className="border-0 pt-1 mt-2 rounded"
      loading="lazy"
      preview
      style={{
        maxWidth: "100%",
        maxHeight: "200px",
        objectFit: "contain",
      }}
    />
  );
};

const ReportMediaItemHeader = ({ name, subtitle }) => (
  <div>
    <h5 className="mb-0">
      {name.length > 15 ? `${name.substring(0, 16)}...` : name}
    </h5>
    <span className="text-muted">{subtitle}</span>
  </div>
);

const ReportAudioPreview = ({
  data,
  authToken,
  api,
  onDownload,
  isDownloading,
}) => {
  const [src, setSrc] = useState(data?.path || null);
  const [loading, setLoading] = useState(false);
  const blobFallbackAttemptedRef = React.useRef(false);

  useEffect(() => {
    setSrc(data?.path || null);
    blobFallbackAttemptedRef.current = false;
  }, [data?.path, data?.report_id]);

  useEffect(() => {
    return () => {
      if (src?.startsWith("blob:")) {
        URL.revokeObjectURL(src);
      }
    };
  }, [src]);

  const loadAudioSrc = async (forceBlob = false) => {
    if (loading) {
      return src;
    }

    if (src && !forceBlob) {
      return src;
    }

    setLoading(true);
    try {
      const response = await apiClient.get(
        `${api}reports-media/download/${data.report_id}`,
        {
          responseType: "blob",
          headers: authToken
            ? { Authorization: `Bearer ${authToken}` }
            : {},
        }
      );

      const objectUrl = URL.createObjectURL(response.data);
      setSrc((previous) => {
        if (previous?.startsWith("blob:")) {
          URL.revokeObjectURL(previous);
        }
        return objectUrl;
      });
      return objectUrl;
    } catch (error) {
      message.error("Failed to load audio");
      console.error(error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handlePlaybackError = async () => {
    if (blobFallbackAttemptedRef.current) {
      return;
    }

    blobFallbackAttemptedRef.current = true;
    await loadAudioSrc(true);
  };

  return (
    <div style={{ width: "100%", padding: "8px 4px" }}>
      <AudioMessagePlayer
        src={src}
        loading={loading}
        isDownloading={isDownloading}
        onRequestLoad={() => loadAudioSrc(false)}
        onPlaybackError={handlePlaybackError}
        onDownload={onDownload}
        compact
      />
    </div>
  );
};

const ReportMediaGridItem = ({ data, authToken, api, parentNumber }) => {
  const [openVideo, setOpenVideo] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const mediaPath = data?.path;
  const mimeType = data?.type || "";
  const messagesType = data?.messages_type || "";
  const subtitle = data?.profile_name || data?.wa_id || "Chat media";
  const isAudio = isAudioMedia(mimeType, messagesType);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadReportMediaFile({
        api,
        authToken,
        reportId: data.report_id,
        fileName: data.name,
        fallbackUrl: mediaPath,
      });
    } catch (error) {
      message.error("Failed to download file");
      console.error(error);
    } finally {
      setDownloading(false);
    }
  };

  const renderMediaContent = () => {
    if (mimeType.startsWith("image/")) {
      return (
        <ReportImagePreview
          data={data}
          api={api}
          authToken={authToken}
          parentNumber={parentNumber}
        />
      );
    }

    if (isAudio) {
      return (
        <ReportAudioPreview
          data={data}
          authToken={authToken}
          api={api}
          onDownload={handleDownload}
          isDownloading={downloading}
        />
      );
    }

    const IconMap = {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
        FileExcelOutlined,
      "application/pdf": FilePdfOutlined,
    };

    let Icon = IconMap[mimeType];
    if (!Icon) {
      if (mimeType.startsWith("video/")) {
        Icon = PlayCircleOutlined;
      } else {
        Icon = FileOutlined;
      }
    }

    return (
      <span
        style={{
          fontSize: "5rem",
          cursor: mimeType.startsWith("video/") ? "pointer" : "default",
        }}
      >
        <Icon
          onClick={
            mimeType.startsWith("video/")
              ? () => setOpenVideo(true)
              : undefined
          }
        />
      </span>
    );
  };

  return (
    <>
      <Modal
        destroyOnClose
        maskClosable={false}
        open={openVideo}
        onCancel={() => setOpenVideo(false)}
        footer={null}
      >
        <Flex justifyContent="center" flexDirection="column">
          <h4 style={{ width: "90%" }}>{data?.name}</h4>
          <div className="player">
            <ReactPlayer
              playing
              url={mediaPath}
              controls
              width="100%"
              height="auto"
              className="rounded"
            />
          </div>
        </Flex>
      </Modal>
      <Card
        style={{ position: "relative", border: "1px solid #d9d9d9" }}
        cover={
          <div
            className="image-box d-flex justify-content-center align-items-center rounded"
            style={{
              height: isAudio ? "auto" : "200px",
              minHeight: isAudio ? "120px" : undefined,
              position: "relative",
            }}
          >
            {renderMediaContent()}
          </div>
        }
      >
        <Card.Meta
          title={
            <Flex alignItems="center" justifyContent="space-between">
              <ReportMediaItemHeader name={data?.name || "Media"} subtitle={subtitle} />
              <EllipsisDropdown
                menu={
                  <Menu>
                    <Menu.Item
                      key="copy-link"
                      onClick={() => copyToClipboard(mediaPath)}
                    >
                      <LinkOutlined />
                      <span className="ml-2">Copy Link</span>
                    </Menu.Item>
                    <Menu.Item
                      key="download"
                      onClick={handleDownload}
                      disabled={downloading}
                    >
                      {downloading ? <LoadingOutlined /> : <DownloadOutlined />}
                      <span className="ml-2">Download</span>
                    </Menu.Item>
                  </Menu>
                }
              />
            </Flex>
          }
        />
      </Card>
    </>
  );
};

export const ReportsMediaTab = ({
  mediaUserId,
  isActive,
  mediaTypeFilter,
  setMediaTypeFilter,
  onCountsChange,
}) => {
  const { authToken, isMobile, UserData, isSupportAgent } = useMyContext();
  const api = process.env.REACT_APP_API_PATH;
  const parentNumber = resolveParentNumber(UserData, isSupportAgent);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [counts, setCounts] = useState({
    all: 0,
    image: 0,
    video: 0,
    document: 0,
  });

  const fetchReportsMedia = useCallback(
    async (pageNum, append = false) => {
      if (!mediaUserId) {
        return;
      }

      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      try {
        const response = await apiClient.get(
          `/media/${mediaUserId}/reports-media`,
          {
            params: {
              page: pageNum,
              per_page: 20,
              type: mediaTypeFilter,
            },
          }
        );

        const payload = response.data || {};
        const nextItems = payload.data || [];

        setItems((prev) => (append ? [...prev, ...nextItems] : nextItems));
        setPage(payload.current_page || pageNum);
        setHasMore(Boolean(payload.has_more));
        setCounts(payload.counts || { all: 0, image: 0, video: 0, document: 0 });
        onCountsChange?.(payload.counts || { all: 0, image: 0, video: 0, document: 0 });
      } catch (error) {
        if (!append) {
          setItems([]);
          setHasMore(false);
        }
        message.error("Failed to fetch reports media");
        console.error(error);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [mediaUserId, mediaTypeFilter, onCountsChange]
  );

  useEffect(() => {
    if (!isActive || !mediaUserId) {
      return;
    }

    fetchReportsMedia(1, false);
  }, [isActive, mediaUserId, mediaTypeFilter, fetchReportsMedia]);

  const handleLoadMore = () => {
    if (!hasMore || loadingMore) {
      return;
    }

    fetchReportsMedia(page + 1, true);
  };

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: isMobile ? "flex-start" : "flex-end",
          alignItems: "center",
          width: "100%",
          marginBottom: "15px",
        }}
      >
        <MediaTypeFilter
          activeType={mediaTypeFilter}
          onChange={setMediaTypeFilter}
          mediaCounts={counts}
          isMobile={isMobile}
        />
      </div>

      {loading && items.length === 0 ? (
        <Flex justifyContent="center" className="py-5">
          <LoadingOutlined style={{ fontSize: 32 }} />
        </Flex>
      ) : (
        <>
          <Row gutter={16}>
            {items.length > 0 ? (
              <AnimatePresence>
                {items.map((item) => (
                  <Col
                    key={`report-media-${item.id}`}
                    xs={12}
                    sm={12}
                    lg={8}
                    xl={8}
                    xxl={4}
                    style={{ marginBottom: 16 }}
                  >
                    <motion.div
                      layout
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.5 }}
                      transition={{ layout: { duration: 0.4, ease: "easeInOut" } }}
                    >
                      <ReportMediaGridItem
                        data={item}
                        authToken={authToken}
                        api={api}
                        parentNumber={parentNumber}
                      />
                    </motion.div>
                  </Col>
                ))}
              </AnimatePresence>
            ) : (
              <Col xs={24} className="d-flex justify-content-center">
                <Empty description="No reports media found" />
              </Col>
            )}
          </Row>

          {hasMore && (
            <Flex justifyContent="center" className="mt-3 mb-4">
              <Button
                type="primary"
                onClick={handleLoadMore}
                loading={loadingMore}
                disabled={loading}
              >
                Load More
              </Button>
            </Flex>
          )}
        </>
      )}
    </>
  );
};
